import type {
  AgenticLogicValidationScenarioInput,
  RuleGraphDto,
} from "@intelligems/ig-types";
import { currencyMinorUnitDigits } from "@intelligems/ig-utils";
import type { ProductOrVariantSummary } from "@src/graphql/hooks/useProductAndVariantSummaries";

import { graphConditionSubjects } from "./conditionSubject";
import {
  buildScenarioContext,
  formatScenarioDelivery,
} from "./scenario-context";
import type { RuleGraphCartInput, RuleGraphCartInputLine } from "./types";

type ScenarioRequest = AgenticLogicValidationScenarioInput["request"];

/** Integer minor units to a currency string ("$50.00"); code-suffixed when unknown. */
export const formatMinorUnits = (amount: number, currency: string): string => {
  const major = amount / 10 ** currencyMinorUnitDigits(currency);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(major);
  } catch {
    // An unknown currency code throws; the amount still means something.
    return `${major} ${currency}`;
  }
};

/**
 * The line's most specific GID — the variant the shopper chose, else the
 * product — which is also the id its summary and picture were looked up by.
 */
export const cartLineLookupId = (line: {
  productId: string;
  variantId: string;
}): string => line.variantId || line.productId;

/**
 * A readable stand-in for a line whose catalog summary has not resolved (or
 * never will — a product the store no longer carries): the GID's numeric
 * tail, so two unresolved lines still tell apart.
 */
const fallbackTitle = (productId: string): string => {
  const tail = productId.split("/").pop() ?? productId;
  return `Product ${tail}`;
};

/**
 * Free-shipping standing in merchant words. `state` is the trust level the
 * rule branches on ("resolved" = facts, "assumed" = a hint, "not-applicable"
 * = no incentive at all), and the gap is what a shipping-keyed rule is
 * reading, so both are said.
 */
const formatShipping = (shipping: ScenarioRequest["shipping"]): string => {
  if (shipping.state === "not-applicable") {
    return "Free shipping: not applicable";
  }
  const trust = shipping.state === "resolved" ? "confirmed" : "assumed";
  const remaining = shipping.remainingToFreeShipping;
  if (typeof remaining === "number") {
    return remaining === 0
      ? `Free shipping earned · ${trust}`
      : `Free shipping: ${formatMinorUnits(remaining, shipping.currency)} away · ${trust}`;
  }
  if (typeof shipping.freeShippingThreshold === "number") {
    return `Free shipping at ${formatMinorUnits(shipping.freeShippingThreshold, shipping.currency)} · ${trust}`;
  }
  return `Free shipping gap unknown · ${trust}`;
};

/** Who is buying, in merchant words. Onsite and post-purchase never know. */
const formatCustomer = (customer: ScenarioRequest["customer"]): string => {
  if (customer === null || customer === undefined) return "Guest";
  const ordersCount = customer.ordersCount;
  if (typeof ordersCount !== "number") {
    return "Signed in · order count unknown";
  }
  if (ordersCount === 0) return "First-time buyer";
  return `Repeat buyer · ${ordersCount} order${ordersCount === 1 ? "" : "s"}`;
};

/**
 * The cart-input panel's content, from the request the run evaluated. Pure:
 * the catalog summaries and pictures arrive already resolved, keyed by the
 * GID {@link cartLineLookupId} names for each line.
 *
 * Context captions are gated on what the graph actually conditions on: a
 * shipping line appears only under a rule with a `shipping`-keyed branch, a
 * delivery line only under a `delivery`-keyed one, a buyer line only under a
 * `customer`-keyed one. Saying more would put a fact on the canvas that no
 * row reads, and invite the merchant to look for the row that does.
 *
 * Null when the request has no cart — the surfaces these rules render on do
 * not exist until something is in the cart, so there is nothing to draw.
 */
export const buildCartInput = ({
  request,
  graph,
  summaryByGid,
  imageUrlByGid,
}: {
  request: ScenarioRequest;
  graph: RuleGraphDto;
  summaryByGid: Record<string, ProductOrVariantSummary>;
  imageUrlByGid: Record<string, string | null>;
}): RuleGraphCartInput | null => {
  const cart = request.cart;
  if (cart === null || cart === undefined) return null;

  const lines: RuleGraphCartInputLine[] = cart.lines.map((line) => {
    const id = cartLineLookupId(line);
    const summary = summaryByGid[id];
    return {
      id,
      title: summary?.title ?? fallbackTitle(line.productId),
      variantTitle: summary?.variantTitle ?? null,
      quantity: line.quantity,
      unitPrice: formatMinorUnits(line.unitPrice, cart.currency),
      imageUrl: imageUrlByGid[id] ?? null,
    };
  });

  const subjects = graphConditionSubjects(graph);
  const { delivery } = buildScenarioContext(request);

  return {
    lines,
    itemCount: cart.itemCount,
    subtotal: formatMinorUnits(cart.subtotal, cart.currency),
    surface: request.surface,
    ...(subjects.includes("shipping")
      ? { shipping: formatShipping(request.shipping) }
      : {}),
    ...(subjects.includes("delivery") && delivery !== undefined
      ? { delivery: formatScenarioDelivery(delivery) }
      : {}),
    ...(subjects.includes("customer")
      ? { customer: formatCustomer(request.customer) }
      : {}),
  };
};
